import { useTranslationContext } from "context/translation";
import { FC, useState } from "react";
import { Image } from "react-datocms/image";
import { StructuredText } from "react-datocms/structured-text";
import { ProductBySlugQuery } from "services/datocms/generated";

type ProductNutritionInformationProps = {
  nutritionData: NonNullable<ProductBySlugQuery["product"]>["nutrition"];
  productTitle: string;
};

export const ProductNutritionInformation: FC<
  ProductNutritionInformationProps
> = ({ nutritionData, productTitle }) => {
  const [open, setOpen] = useState(false);

  const { nutritionInformation, ingredients } = useTranslationContext();

  return (
    <>
      <div className="h-20 bg-white border-t border-b border-neutral-200 justify-start items-center gap-6 inline-flex">
        <div className="grow shrink basis-0 h-[33px] justify-start items-center gap-2.5 flex">
          <div className="grow shrink basis-0 text-blue-950 text-[22px] font-semibold">
            {nutritionInformation}
          </div>
        </div>
        <div
          onClick={() => setOpen(true)}
          className="w-12 h-12 rounded-full border border-blue-950 justify-center items-center gap-2 flex cursor-pointer"
          bi-type="Nutrition information"
          bi-id={productTitle}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="12"
            viewBox="0 0 18 12"
            className="fill=blue-950"
          >
            <path d="M17.0469 6.66406L13.2969 10.4141L12.6328 11.0781L11.3047 9.75L11.9688 9.08594L14.1172 6.9375H1.22656H0.289062V5.0625H1.22656H14.1172L11.9688 2.91406L11.3047 2.25L12.6328 0.921875L13.2969 1.58594L17.0469 5.33594L17.7109 6L17.0469 6.66406Z" />
          </svg>
        </div>
      </div>
      {open && (
        <div
          className="fixed top-0 right-0 inset-0 overflow-auto z-[200] bg-black bg-opacity-20"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setOpen(false);
            }
          }}
        >
          <div className="absolute top-0 right-0 h-screen overflow-auto w-screen md:w-[600px] p-6 bg-white flex flex-col gap-10">
            <div className="self-stretch flex justify-end items-center">
              <div
                className="w-6 h-6 cursor-pointer"
                onClick={() => setOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M1.07812 11.8894L0.110596 10.9219L5.03247 6L0.110596 1.07812L1.07812 0.110596L6 5.03247L10.9219 0.110596L11.8894 1.07812L6.96752 6L11.8894 10.9219L10.9219 11.8894L6 6.96752L1.07812 11.8894Z"
                    className="fill-gray-900"
                  />
                </svg>
              </div>
            </div>
            <h2 className=" text-gray-900 text-[21px] lg:text-2xl font-semibold">
              {nutritionInformation}
            </h2>
            {nutritionData?.image?.responsiveImage && (
              <div className="max-w-[400px]">
                <Image data={nutritionData?.image?.responsiveImage} />
              </div>
            )}
            <div>
              <h4 className="text-black text-base font-semibold leading-[21px] mb-2">
                {ingredients}
              </h4>
              {nutritionData?.ingredients && (
                <div className="text-gray-500 text-xs font-medium">
                  <StructuredText data={nutritionData?.ingredients.value} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
